import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ParkingMainRateDto,
  ParkingMainRateMaster,
  ParkingPenaltyRateDetail,
  ParkingPenaltyRateOption,
  ParkingSubRateDetail,
  ParkingSubRateOption,
} from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-main-rate',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-main-rate.component.html',
  styleUrl: './modal-parking-main-rate.component.css',
})
export class ModalParkingMainRateComponent implements OnInit {
  @Input() itemId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingMainRateMaster;
  subRateOptions: ParkingSubRateOption[] = [];
  penaltyRateOptions: ParkingPenaltyRateOption[] = [];
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyMainRate();
    this.loadOptions();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  onSubRateChange(
    field: 'rateCustomerCar' | 'rateCustomerBike' | 'rateTenantCar' | 'rateTenantBike' | 'rateHolidayCar' | 'rateHolidayBike',
    idField: 'rateCustomerCarId' | 'rateCustomerBikeId' | 'rateTenantCarId' | 'rateTenantBikeId' | 'rateHolidayCarId' | 'rateHolidayBikeId',
  ): void {
    this.formData[field] = this.findSubRate(this.formData[idField]);
  }

  onPenaltyChange(
    field: 'penaltyCar' | 'penaltyBike',
    idField: 'penaltyCarId' | 'penaltyBikeId',
  ): void {
    this.formData[field] = this.findPenalty(this.formData[idField]);
  }

  onSave(): void {
    if (!this.formData.mainRateId?.trim()) { this.toast.error('กรุณาระบุ Main Rate ID'); return; }
    if (!this.formData.name?.trim()) { this.toast.error('กรุณาระบุชื่อ'); return; }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) { this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน'); return; }

    const payload = this.mapper.mapMainRatePayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingMainRate(this.itemId, payload)
      : this.api.insertParkingMainRate(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadOptions(): void {
    this.api.getParkingSubRateOptions().subscribe({
      next: (res) => {
        this.subRateOptions = this.mapper.mapSubRateOptions(res);
        this.syncSelectedDetailsFromOptions();
      },
      error: () => { this.subRateOptions = []; },
    });
    this.api.getParkingPenaltyRateOptions().subscribe({
      next: (res) => {
        this.penaltyRateOptions = this.mapper.mapPenaltyRateOptions(res);
        this.syncSelectedDetailsFromOptions();
      },
      error: () => { this.penaltyRateOptions = []; },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingMainRateById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapMainRate(res as ParkingMainRateDto);
        this.syncSelectedDetailsFromOptions();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  private syncSelectedDetailsFromOptions(): void {
    if (!this.formData) return;
    if (!this.formData.rateCustomerCar) this.formData.rateCustomerCar = this.findSubRate(this.formData.rateCustomerCarId);
    if (!this.formData.rateCustomerBike) this.formData.rateCustomerBike = this.findSubRate(this.formData.rateCustomerBikeId);
    if (!this.formData.rateTenantCar) this.formData.rateTenantCar = this.findSubRate(this.formData.rateTenantCarId);
    if (!this.formData.rateTenantBike) this.formData.rateTenantBike = this.findSubRate(this.formData.rateTenantBikeId);
    if (!this.formData.rateHolidayCar) this.formData.rateHolidayCar = this.findSubRate(this.formData.rateHolidayCarId);
    if (!this.formData.rateHolidayBike) this.formData.rateHolidayBike = this.findSubRate(this.formData.rateHolidayBikeId);
    if (!this.formData.penaltyCar) this.formData.penaltyCar = this.findPenalty(this.formData.penaltyCarId);
    if (!this.formData.penaltyBike) this.formData.penaltyBike = this.findPenalty(this.formData.penaltyBikeId);
  }

  private findSubRate(rateId: string | null | undefined): ParkingSubRateDetail | null {
    if (!rateId?.trim()) return null;
    const found = this.subRateOptions.find((o) => o.rateId === rateId.trim());
    return found ? { ...found } : null;
  }

  private findPenalty(penaltyId: string | null | undefined): ParkingPenaltyRateDetail | null {
    if (!penaltyId?.trim()) return null;
    const found = this.penaltyRateOptions.find((o) => o.penaltyId === penaltyId.trim());
    return found ? { ...found } : null;
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
